import { useEffect, useMemo, useRef } from 'react';
import QRCode from 'qrcode';
import { cn } from '../../../lib/utils';
import type { CharlieQrCodeProps } from './index.js';

/** Quiet zone around the code, in modules. */
const MARGIN = 2;

/** Above this length the centre image is skipped: modules get too small. */
const MAX_IMAGE_DATA_LENGTH = 600;

function hasCentreImage(data: string, image?: string): boolean {
  return !!(image && data && data.length < MAX_IMAGE_DATA_LENGTH);
}

/**
 * Number of modules per side of the code `CharlieQrCode` draws for `data`.
 *
 * Uses the same error-correction level as the component: H when `image` will
 * be drawn in the centre, M otherwise. Use it to size the code or its
 * surroundings by module, for example 3px per module. Throws when `data` is
 * too long to fit in any QR version.
 */
export function getCharlieQrModuleCount(data: string, image?: string): number {
  return QRCode.create(data, {
    errorCorrectionLevel: hasCentreImage(data, image) ? 'H' : 'M',
  }).modules.size;
}

/**
 * Compute the optimal display size for a QR code based on module count.
 *
 * Each module needs ~3px minimum on screen to be reliably scannable.
 * We round up to the nearest Tailwind size token for consistency.
 *
 * | Modules | Min px | Tailwind |
 * |---------|--------|----------|
 * | ≤ 33    | ~100   | w-48 (192px) — generous for simple codes |
 * | ≤ 57    | ~170   | w-52 (208px) |
 * | ≤ 73    | ~220   | w-56 (224px) |
 * | ≤ 93    | ~280   | w-72 (288px) |
 * | > 93    | ~320   | w-80 (320px) |
 */
function getQrSizeClass(moduleCount: number | null): string {
  if (moduleCount === null) return 'w-56';
  if (moduleCount <= 33) return 'w-48';
  if (moduleCount <= 57) return 'w-52';
  if (moduleCount <= 73) return 'w-56';
  if (moduleCount <= 93) return 'w-72';
  return 'w-80';
}

/** Canvas pixels per module: fills `size` at the device pixel ratio when set. */
function getScale(
  data: string,
  moduleCount: number | null,
  size?: number,
): number {
  if (size && moduleCount) {
    const pixels = size * (window.devicePixelRatio || 1);
    return Math.max(1, Math.ceil(pixels / (moduleCount + MARGIN * 2)));
  }
  return data.length < 60 ? 8 : data.length < 120 ? 6 : 4;
}

/**
 * CharlieQrCode — React equivalent of Rocket's `RocketQrCode`.
 *
 * Renders a QR code onto an HTML `<canvas>` element with optional center-image
 * overlay. Automatically sizes itself based on data complexity to ensure
 * scanability, unless you set `size`.
 *
 * ## Usage
 *
 * ```tsx
 * import {
 *   CharlieQrCode,
 *   getCharlieQrModuleCount,
 * } from '@ver.id/libs.ui.charlie';
 *
 * // Auto-sized based on data complexity
 * <CharlieQrCode data="https://example.com" />
 *
 * // With a center logo
 * <CharlieQrCode data={payload} image="https://example.com/logo.png" />
 *
 * // Fixed pixel size
 * <CharlieQrCode data={payload} size={240} />
 *
 * // 3px per module, kept between 188px and 270px
 * const modules = getCharlieQrModuleCount(payload, logo);
 * const size = Math.min(270, Math.max(188, (modules + 8) * 3));
 * <CharlieQrCode data={payload} image={logo} size={size} />
 *
 * // Override auto-sizing with a custom class
 * <CharlieQrCode data="hello" className="w-64" />
 * ```
 *
 * ## Gotchas
 *
 * - **Center image needs data under 600 characters**: longer payloads produce
 *   modules too small for the overlay, so the image is skipped. With an image
 *   the code switches to error-correction level H, which makes it denser.
 *
 * - **CORS on images**: The centre image is loaded with `crossOrigin = 'anonymous'`.
 *   Make sure the image server sends appropriate CORS headers, otherwise the
 *   canvas will be tainted and the image won't render.
 *
 * - **`size` wins over `className` widths**: it is applied as an inline style.
 */
export function CharlieQrCode({
  data,
  image,
  size,
  className,
  testId,
}: CharlieQrCodeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const imageEnabled = hasCentreImage(data, image);
  const moduleCount = useMemo(() => {
    if (!data) return null;
    try {
      return getCharlieQrModuleCount(data, image);
    } catch {
      return null;
    }
  }, [data, image]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;

    const scale = getScale(data, moduleCount, size);

    let cancelled = false;

    (async () => {
      try {
        await QRCode.toCanvas(canvas, data, {
          scale,
          margin: MARGIN,
          errorCorrectionLevel: imageEnabled ? 'H' : 'M',
          color: {
            dark: '#000000ff',
            light: '#00000000',
          },
        });

        if (cancelled) return;

        canvas.style.width = size ? `${size}px` : '';
        canvas.style.height = '';

        if (imageEnabled) {
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          const img = await loadImage(image);
          if (cancelled) return;

          const rawImgSize = Math.floor(canvas.width * 0.15);
          const imgSize = Math.round(rawImgSize / scale) * scale;
          const padding = scale;
          const clearSize = imgSize + padding * 2;

          const cx = Math.round((canvas.width - clearSize) / 2 / scale) * scale;
          const cy =
            Math.round((canvas.height - clearSize) / 2 / scale) * scale;

          ctx.clearRect(cx, cy, clearSize, clearSize);

          const finalImageWidth = Math.min(
            imgSize,
            (imgSize * img.width) / img.height,
          );
          const finalImageHeight = Math.min(
            imgSize,
            (imgSize * img.height) / img.width,
          );

          const ix = cx + padding + (imgSize - finalImageWidth) / 2;
          const iy = cy + padding + (imgSize - finalImageHeight) / 2;
          ctx.drawImage(img, ix, iy, finalImageWidth, finalImageHeight);
        }
      } catch (error) {
        console.error('Failed to generate QR code:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data, image, imageEnabled, moduleCount, size]);

  return (
    <canvas
      ref={canvasRef}
      data-testid={testId}
      className={cn(
        className ?? (size ? undefined : getQrSizeClass(moduleCount)),
      )}
    />
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
